import { ref, onMounted, onUnmounted } from 'vue'

export interface SwipeDirection {
  left: boolean
  right: boolean
  up: boolean
  down: boolean
}

export interface SwipeGestureOptions {
  threshold?: number // Minimum distance for swipe detection
  timeout?: number // Maximum time for swipe gesture
  onSwipe?: (direction: SwipeDirection) => void
}

export function useSwipeGesture(options: SwipeGestureOptions = {}) {
  const { threshold = 50, timeout = 300, onSwipe = () => {} } = options

  const startX = ref(0)
  const startY = ref(0)
  const startTime = ref(0)
  const isSwipeActive = ref(false)

  const direction = ref<SwipeDirection>({
    left: false,
    right: false,
    up: false,
    down: false,
  })

  const reset = () => {
    direction.value = {
      left: false,
      right: false,
      up: false,
      down: false,
    }
    isSwipeActive.value = false
  }

  const onTouchStart = (event: TouchEvent) => {
    if (event.touches.length === 1) {
      startX.value = event.touches[0].clientX
      startY.value = event.touches[0].clientY
      startTime.value = Date.now()
      isSwipeActive.value = true
      reset()
    }
  }

  const onTouchEnd = (event: TouchEvent) => {
    if (!isSwipeActive.value || event.changedTouches.length !== 1) return

    const endX = event.changedTouches[0].clientX
    const endY = event.changedTouches[0].clientY
    const endTime = Date.now()

    const deltaX = endX - startX.value
    const deltaY = endY - startY.value
    const deltaTime = endTime - startTime.value

    // Check if swipe is within time threshold
    if (deltaTime > timeout) {
      reset()
      return
    }

    // Check if swipe distance meets threshold
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(deltaY)

    if (absDeltaX < threshold && absDeltaY < threshold) {
      reset()
      return
    }

    // Determine primary swipe direction
    if (absDeltaX > absDeltaY) {
      // Horizontal swipe
      if (deltaX > 0) {
        direction.value.right = true
      } else {
        direction.value.left = true
      }
    } else {
      // Vertical swipe
      if (deltaY > 0) {
        direction.value.down = true
      } else {
        direction.value.up = true
      }
    }

    onSwipe(direction.value)
    reset()
  }

  const onTouchCancel = () => {
    reset()
  }

  onMounted(() => {
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchend', onTouchEnd, { passive: true })
    document.addEventListener('touchcancel', onTouchCancel, { passive: true })
  })

  onUnmounted(() => {
    document.removeEventListener('touchstart', onTouchStart)
    document.removeEventListener('touchend', onTouchEnd)
    document.removeEventListener('touchcancel', onTouchCancel)
  })

  return {
    direction: direction.value,
    isSwipeActive,
    reset,
  }
}
